import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';

export enum Actions {
  update = 'UPDATE',
  create = 'CREATE',
  delete = 'DELETE',
  bulkUpdate = 'BULK_UPDATE',
  createFromTemplate = 'CREATE_FROM_TEMPLATE',
}

export interface ActivityLog {
  id: string;
  changedAt: string;
  changedByName: string;
  changesCount: number;
  entityName: string;
  actorType: string;
  updateSource: string;
  actionType: string;
  planName: string;
  sourceEntryPoint: string;
}

interface ActivityLogPayload {
  id?: number;
  changed_at?: string;
  changed_by_name?: string;
  changes_count?: number;
  entity_name?: string;
  actor_type?: string;
  update_source?: string;
  action_type?: string;
  plan_name?: string;
  source_entry_point?: string;
}

const formatData = (data: ActivityLogPayload[]) =>
  data.map((item) => ({
    id: toString(item?.id || ''),
    changedAt: item?.changed_at || '',
    changedByName: item?.changed_by_name || '',
    changesCount: item?.changes_count || '',
    entityName: item?.entity_name || '',
    actorType: item?.actor_type || '',
    updateSource: item?.update_source || '',
    actionType: item?.action_type || '',
    planName: item?.plan_name || '',
    sourceEntryPoint: item?.source_entry_point || '',
  }));

interface Props {
  employeeId?: string;
  lockboxId?: string;
  organizationId?: string;
  planId?: string;
  healthPlanId?: string;
  roleId?: string;
  payoutDefinitionId?: string;
  domainType?: string;
  search?: string;
  actions?: string[];
  fields?: string[];
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
  sort?: string;
  claimId?: string;
  disabled?: boolean;
}
const useGetActivityLogsQuery = ({
  employeeId,
  lockboxId,
  planId,
  healthPlanId,
  roleId,
  payoutDefinitionId,
  search,
  actions,
  fields,
  dateFrom,
  dateTo,
  page,
  size,
  sort,
  organizationId,
  domainType,
  claimId,
  disabled,
}: Props) => {
  const { data, isLoading, isFetching, isSuccess } = useQuery(
    [
      API_URL.ACTIVITY_LOGS,
      employeeId,
      search,
      actions,
      claimId,
      planId,
      healthPlanId,
      roleId,
      payoutDefinitionId,
      fields,
      dateFrom,
      dateTo,
      page,
      size,
      sort,
      organizationId,
      lockboxId,
    ],
    () =>
      api.get<{
        content: ActivityLogPayload[];
        totalPages: number;
        totalElements: number;
        // TODO: this  DTO type is missing
      }>(API_URL.ACTIVITY_LOGS, {
        params: {
          ...(claimId ? { claim_id: claimId } : {}),
          ...(employeeId ? { employee_id: employeeId } : {}),
          ...(lockboxId ? { lockbox_id: lockboxId } : {}),
          ...(organizationId ? { organization_id: organizationId } : {}),
          ...(planId ? { plan_id: planId } : {}),
          ...(healthPlanId ? { entity_id: healthPlanId } : {}),
          ...(roleId ? { entity_id: roleId } : {}),
          ...(payoutDefinitionId ? { entity_id: payoutDefinitionId } : {}),
          ...(search ? { search_term: search } : {}),
          ...(actions?.length
            ? {
                action_types: actions.toString(),
              }
            : {}),
          ...(fields?.length
            ? {
                field_names: fields.toString(),
              }
            : {}),
          ...(dateFrom && dateTo
            ? {
                date_from: dateFrom,
                date_to: dateTo,
              }
            : {}),
          ...(page ? { page: page - 1 } : {}),
          ...(size ? { size } : {}),
          ...(sort ? { sort } : {}),
          ...(domainType ? { domain_types: domainType } : {}),
        },
      }),
    {
      enabled: !disabled,
    }
  );

  const logs = useMemo(() => formatData(data?.data?.content || []) as ActivityLog[], [data?.data?.content]);
  const logsIds = useMemo(() => logs?.map((log) => log?.id), [logs]);

  return {
    logs,
    logsIds,
    total: data?.data?.totalElements || 0,
    isLoading,
    isFetching,
    isSuccess,
  };
};

export default useGetActivityLogsQuery;
